import { Notify } from 'quasar'
import config from 'src/config'
import Payment from 'src/models/payment/Payment'
import { logRequest } from 'src/models/payment-connector/PaymentConnectorLog'
import Site from 'src/models/site/Site'
import { reactive } from 'vue'

/**
 * @param {import('@quasar/app-webpack').BootFileParams} params
 */
export default async ({ app }) => {
  let stripe = null
  let stripeTerminal = null
  let readersDiscovered = false
  let reconnectWorker = 0

  async function includeScript (url) {
    return new Promise((resolve, reject) => {
      const documentTag = document, tag = 'script',
        object = documentTag.createElement(tag),
        scriptTag = documentTag.getElementsByTagName(tag)[0]
      object.src = 'https://' + url
      object.addEventListener('load', function (e) {
        resolve()
      }, false)
      scriptTag.parentNode.insertBefore(object, scriptTag)
    })
  }

  // Initializes the Stripe SDK
  async function configureStripe (standardConnectAccountId = null) {
    const effectivePublicKey = _.isNil(standardConnectAccountId) ? config.stripeAPIToken : config.stripeStandardConnectApiToken

    if (stripe && stripe._apiKey === effectivePublicKey) {
      return stripe
    }

    if (!window.Stripe) {
      await includeScript('js.stripe.com/v3/')
    }

    if (_.isNil(standardConnectAccountId)) {
      stripe = window.Stripe(effectivePublicKey)
    } else {
      stripe = window.Stripe(effectivePublicKey, {
        stripeAccount: standardConnectAccountId
      })
    }
    window.$stripe = stripe

    return stripe
  }

  let currentSite = null
  async function configureStripeTerminal () {
    if (stripeTerminal) return stripeTerminal // Already configured

    readersDiscovered = false

    if (!window.StripeTerminal) {
      await includeScript('js.stripe.com/terminal/v1/')
    }

    currentSite = new Site({
      options: {
        queryName: 'currentSite',
        fields: ['id', 'stripeConnectionToken']
      }
    })

    stripeTerminal = window.StripeTerminal.create({
      onFetchConnectionToken: fetchConnectionToken,
      onUnexpectedReaderDisconnect: handleTerminalDisconnect
    })
    window.terminal = stripeTerminal

    return stripeTerminal
  }

  async function fetchConnectionToken () {
    await currentSite.fetch(true)
    logRequest('fetchConnectionToken', JSON.stringify({ siteId: currentSite.id }), 'Fetched connection token.')
    return currentSite.stripeConnectionToken
  }

  async function discoverTerminalReaders (stripeLocationId, force = false) {
    if (!stripeTerminal) await this.configureStripeTerminal()

    if (!stripeLocationId) {
      status.error = 'No stripe location has been configured.'
      return { error: status.error, recoverable: false }
    }

    if (!force && readersDiscovered) return { readers: payments.discoveredReaders }

    const config = { simulated: false, location: stripeLocationId }
    const discoverResult = await stripeTerminal.discoverReaders(config)
    if (discoverResult.error) {
      logRequest('discoverTerminalReaders', JSON.stringify(discoverResult), discoverResult.error)
      status.error = discoverResult.error
      return { error: discoverResult.error.message, recoverable: true }
    }
    const siteScopedReaders = discoverResult.discoveredReaders
    if (siteScopedReaders.length === 0) {
      readersDiscovered = true
      return { readers: siteScopedReaders }
    } else {
      payments.discoveredReaders = siteScopedReaders
      readersDiscovered = true
      return { readers: siteScopedReaders }
    }
  }

  async function terminalConnectionInitalizer (registerClient, stripeLocationId, force = false, displayNotifications = true, specificReader = null) {
    await this.configureStripeTerminal()

    const readerResult = await discoverTerminalReaders(stripeLocationId, force)
    if (readerResult.error) {
      logRequest('terminalConnectionInitalizer', JSON.stringify(readerResult), 'Error discovering readers.')
      status.error = readerResult.error.message || readerResult.error
      return { connected: status.connected, error: status.error, recoverable: true }
    }

    if ((!registerClient?.stripeTerminalSerialNumber && !registerClient?.registerStation?.defaultStripeReaderSerialNumber) && !specificReader) {
      logRequest('terminalConnectionInitalizer', JSON.stringify(readerResult), 'No reader configured for this register client.')
      status.error = 'No reader configured for this register client.'
      return { connected: status.connected, error: status.error, recoverable: false, notConfigured: true }
    }

    if (!stripeLocationId) {
      logRequest('terminalConnectionInitalizer', JSON.stringify(readerResult), 'No stripe location has been configured for this site.')
      status.error = 'No stripe location has been configured for this site.'
      return { connected: status.connected, error: status.error, recoverable: false }
    }

    const discoveredReaders = _.filter(readerResult.readers, { location: stripeLocationId })

    if (discoveredReaders.length === 0) {
      logRequest('terminalConnectionInitalizer', JSON.stringify(readerResult), 'No readers found for this site.')
      status.error = 'No Readers Exist for this site.'
      return { connected: status.connected, error: status.error, recoverable: false }
    }

    this.discoveredReaders = readerResult.readers

    const matchingReader =
      (specificReader && _.find(this.discoveredReaders,
        reader =>
          reader.serial_number === specificReader.serial_number
      )) ||
      _.find(this.discoveredReaders,
        reader =>
          reader.serial_number === registerClient.stripeTerminalSerialNumber
      ) ||
      _.find(this.discoveredReaders,
        reader =>
          reader.serial_number === registerClient.registerStation.defaultStripeReaderSerialNumber
      )

    if (!matchingReader) {
      status.error = 'Currently selected reader cannot be found, try selecting a different reader'
      logRequest('terminalConnectionInitalizer', JSON.stringify(readerResult), 'Currently selected reader cannot be found, try selecting a different reader')
      return { connected: status.connected, error: status.error, recoverable: false }
    }

    const readerConnection = await connectToReader(matchingReader, force, displayNotifications)

    if (readerConnection.error) {
      status.error = readerConnection.error.message
      logRequest('terminalConnectionInitalizer', JSON.stringify(readerResult), 'Error connecting to reader.')
      return { connected: status.connected, error: status.error, recoverable: true }
    }

    status.error = ''

    return { connected: status.connected, error: null, recoverable: true }
  }

  async function connectToReader (reader, forceReconnect = false, displayNotifications = true) {
    if (reader) {
      status.currentReader = reader
    } else {
      // Support reconnecting to existing reader
      reader = status.currentReader
    }

    const connectedReader = stripeTerminal.getConnectedReader()
    const readerAlreadyConnected = connectedReader?.serial_number === reader.serial_number
    if (!forceReconnect && readerAlreadyConnected) {
      status.connected = true
      return { connected: status.connected }
    }

    if (connectedReader) {
      status.connected = false
      await stripeTerminal.disconnectReader()
    }

    const connectResult = await stripeTerminal.connectReader(reader)

    if (connectResult.error) {
      status.connected = false
      status.error = connectResult.error

      logRequest('connectToReader', JSON.stringify(reader), 'Failed to connect: ' + connectResult.error?.message)
      console.log('Failed to connect: ' + connectResult.error)

      if (displayNotifications) {
        Notify.create({
          message: 'Failed to connect: ' + connectResult.error.message,
          type: 'negative',
          position: 'top'
        })
      }

      return { error: connectResult.error, connected: status.connected }
    } else {
      status.connected = true
      status.error = ''
      logRequest('connectToReader', JSON.stringify(reader), 'Connected to reader.')
      return { connected: status.connected }
    }
  }

  async function cancelExistingTerminalCollectionAttempt () {
    if (stripeTerminal &&
      status.connected &&
      stripeTerminal.collectPaymentMethodAttempt &&
      !stripeTerminal.collectPaymentMethodAttempt.canceled) {
      console.log('CANCELING COLLECTION ATTEMPT')

      try {
        const saveSilent = status.silent
        status.silent = true
        await stripeTerminal.cancelCollectPaymentMethod()
        status.silent = saveSilent
        status.waitingForCustomerPayment = false
      } catch (error) {
        logRequest('cancelExistingTerminalCollectionAttempt', JSON.stringify(error), 'Error canceling collection attempt.')
        console.log('ERROR CANCELING COLLECTION INTENT')
        console.log(error)
      }
    }
  }

  const PAYMENT_CANCELLED_BY_USER_MESSAGE = 'Transaction is cancelled by the user.'
  async function collectTerminalPaymentMethod (clientSecret, skipTipping = true, allowCustomerCancellation = false) {
    status.silent = true
    status.waitingForCustomerPayment = true

    await cancelExistingTerminalCollectionAttempt()
    status.silent = false
    const collectResult = await stripeTerminal.collectPaymentMethod(clientSecret, {
      config_override: {
        skip_tipping: skipTipping,
        enable_customer_cancellation: allowCustomerCancellation
      }
    })

    if (!status.silent) {
      status.waitingForCustomerPayment = false
    }
    if (collectResult.error) {
      if (collectResult.error.message === PAYMENT_CANCELLED_BY_USER_MESSAGE) {
        // We can ignore this error as it is expected
        console.log('collectTerminalPaymentMethod CANCELLED BY USER')
        return { error: collectResult.error }
      }
      status.error = collectResult.error
      logRequest('collectTerminalPaymentMethod', JSON.stringify(collectResult), 'Error collecting payment method.')
      console.log('collectTerminalPaymentMethod ERROR:')
      console.log(collectResult.error)

      if (!status.silent) {
        Notify.create({
          message: 'Collect payment error ' + collectResult.error.message,
          type: 'negative',
          position: 'top'
        })
      }

      return { error: collectResult.error }
    } else {
      status.error = ''
      return { collectResult }
    }
  }

  async function readPaymentMethodFingerprintFromCard (setupIntentSecret, allowCustomerCancellation = false) {
    status.silent = true
    status.waitingForCustomerPayment = true

    await cancelExistingTerminalCollectionAttempt()

    status.silent = false
    const setupResult = await stripeTerminal.collectSetupIntentPaymentMethod(
      setupIntentSecret,
      true,
      {
        enable_customer_cancellation: allowCustomerCancellation
      }
    )

    if (setupResult.error) {
      logRequest('readPaymentMethodFingerprintFromCard', JSON.stringify(setupResult), 'Error collecting setup intent payment method.')
      return { error: setupResult.error }
    }

    const collectResult = await stripeTerminal.confirmSetupIntent(setupResult.setupIntent)

    return { collectResult }
  }

  async function readReusableCard (setupIntentSecret, allowCustomerCancellation = false) {
    status.silent = true
    status.waitingForCustomerPayment = true

    await cancelExistingTerminalCollectionAttempt()

    status.silent = false
    const setupResult = await stripeTerminal.collectSetupIntentPaymentMethod(
      setupIntentSecret,
      true,
      {
        enable_customer_cancellation: allowCustomerCancellation
      }
    )

    let collectResult = null

    if (setupResult.error) {
      logRequest('readReusableCard', JSON.stringify(setupResult), 'Error collecting setup intent.')
      collectResult = setupResult
    } else {
      collectResult = await stripeTerminal.confirmSetupIntent(setupResult.setupIntent)
    }

    if (!status.silent) {
      status.waitingForCustomerPayment = false
    }
    if (collectResult.error) {
      logRequest('readReusableCard', JSON.stringify(collectResult), 'Error collecting reusable card.')
      console.log('readReusableCard ERROR:')
      console.log(collectResult.error)

      if (!status.silent) {
        Notify.create({
          message: 'Collect reusable card error ' + collectResult.error.message,
          type: 'negative',
          position: 'top'
        })
      }

      return { error: collectResult.error }
    } else {
      console.log('readReusableCard SUCCESS')
      return { collectResult }
    }
  }

  const INTERAC_CURRENCY = 'cad'
  async function interacRefund (refundOption) {
    const result = await stripeTerminal.collectRefundPaymentMethod(
      refundOption.stripeChargeId,
      refundOption.amount * 100,
      INTERAC_CURRENCY
    )

    if (result.error) {
      logRequest('interacRefund', JSON.stringify(result), 'Error collecting refund payment method.')
      return { success: false, error: result.error }
    }

    const refund = await stripeTerminal.processRefund()
    if (refund.error) {
      logRequest('interacRefund', JSON.stringify(refund), 'Error processing refund.')
      return { success: false, error: refund.error }
    } else {
      return { success: true, result: refund }
    }
  }

  async function processTerminalPayment (collectResult, displayErrors = true) {
    status.processingPayment = true
    const processResult = await stripeTerminal.processPayment(collectResult.paymentIntent)

    status.processingPayment = false
    if (processResult.error) {
      logRequest('processTerminalPayment', JSON.stringify(processResult), 'Error processing terminal payment.')
      // Placeholder for handling result.error
      console.log('processTerminalPayment ERROR:')
      console.log(processResult.error)

      displayErrors && Notify.create({
        message: 'Process payment error ' + processResult.error.message,
        type: 'negative',
        position: 'top'
      })
      return { success: false, error: processResult.error.message }
    } else if (processResult.paymentIntent) {
      // const paymentIntentId = processResult.paymentIntent.id
      console.log('processTerminalPayment SUCCESS')
      console.log(processResult.paymentIntent)

      return { success: true, error: null }
    }
  }

  async function handleTerminalDisconnect () {
    const workerNumber = ++reconnectWorker
    if (status.connected) {
      logRequest('handleTerminalDisconnect', JSON.stringify(status), 'Disconnected from terminal.')
    }

    status.reconnecting = true
    status.connected = false

    if (payments.disconnectHandler) {
      await payments.disconnectHandler()
    }

    if (workerNumber === reconnectWorker) {
      status.reconnecting = false
    }
  }

  async function refundPayment (paymentIntent) {
    const payment = new Payment({
      stripePaymentIntentId: paymentIntent.id
    })

    return payment.refund()
  }

  const status = reactive({
    currentReader: null,
    connected: null,
    waitingForCustomerPayment: false,
    processingPayment: false,
    message: '',
    error: '',
    reconnecting: false
  })

  const payments = {
    configureStripe,
    configureStripeTerminal,
    discoverTerminalReaders,
    connectToReader,

    collectTerminalPaymentMethod,
    processTerminalPayment,
    cancelExistingTerminalCollectionAttempt,

    readReusableCard,
    terminalConnectionInitalizer,
    readPaymentMethodFingerprintFromCard,
    handleTerminalDisconnect,

    refundPayment,
    interacRefund,
    discoveredReaders: [],

    status,

    disconnectHandler: null // Can be specified by user
  }

  app.config.globalProperties.$payments = payments
}
